import classNames from "classnames";
import { CSSProperties, memo, useId } from "react";
import { Flex } from "../flex";
import { Text } from "../text";
import { TextColorValue, TextSizeValue } from "../theme/typography";
import { DisabledTooltip } from "../tooltip/disabled-tooltip";
import * as redoRadioCss from "./redo-radio.module.css";

export const redoRadioButtonSize = ["xs", "sm", "md"] as const;
export type RedoRadioButtonSize = (typeof redoRadioButtonSize)[number];

export interface RedoRadioButtonProps {
  "data-dd-action-name"?: string;
  ariaLabel?: string;
  label?: string | React.ReactNode;
  /** Rendered inline after the label, for badges and other trailing markers. */
  labelBadge?: React.ReactNode;
  description?: string;
  bordered?: boolean;
  selected: boolean;
  onSelect(value: boolean): void;
  disabled?: boolean;
  disabledTooltip?: string;
  size?: RedoRadioButtonSize;
  dataTestId?: string;
  /**
   * Custom styling for merchant branding - this should only be used by BrandKit components
   */
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: CSSProperties;
  uiSelector?: string;
  fullWidth?: boolean;
}

export const RedoRadioButton = memo(function RedoRadioButton({
  ariaLabel,
  label,
  labelBadge,
  description,
  bordered,
  selected,
  onSelect,
  disabled,
  disabledTooltip,
  size = "xs",
  dataTestId,
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
  uiSelector,
  fullWidth,
  "data-dd-action-name": actionName,
}: RedoRadioButtonProps) {
  const inputId = `redo-radio-button-${useId()}`;
  const labelTextColor: TextColorValue =
    (disabled && "disabled") || (selected && "primary") || "secondary";
  const descriptionTextColor: TextColorValue =
    (disabled && "disabled") || "secondary";

  const showDescriptors = !!(label || description || labelBadge);

  const textStyle = dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
    ? {
        color: dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.color,
        fontFamily:
          dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.fontFamily,
        fontSize:
          dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.fontSize,
        fontWeight:
          dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.fontWeight,
      }
    : undefined;

  const labelNode =
    typeof label === "string" ? (
      <Text
        className={redoRadioCss.text}
        fontSize={sizeToLabelTextSize[size]}
        fontWeight={selected ? "medium" : "regular"}
        style={textStyle}
        textColor={
          !dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
            ? labelTextColor
            : undefined
        }
      >
        {label}
      </Text>
    ) : (
      label
    );

  const radio = (
    <Flex
      align="flex-start"
      as="label"
      className={classNames(
        redoRadioCss.radioButton,
        disabled && redoRadioCss.disabled,
        bordered && redoRadioCss.border,
        selected && bordered && redoRadioCss.selected,
      )}
      data-arbiter="RedoRadioButton"
      data-ui={uiSelector}
      labelProps={{ htmlFor: inputId }}
      w={fullWidth ? "full" : undefined}
    >
      <Flex align="center" gap="none" pt={showDescriptors ? "xxs" : "none"}>
        <input
          aria-label={ariaLabel}
          checked={selected}
          className={classNames(redoRadioCss.radioInput)}
          data-dd-action-name={actionName}
          data-testid={dataTestId}
          disabled={!!disabled}
          id={inputId}
          onChange={(event) => {
            onSelect(event.target.checked);
          }}
          type="radio"
        />
        <Flex
          align="center"
          as="span"
          className={classNames(
            redoRadioCss.circle,
            selected && redoRadioCss.selected,
            sizeToClass[size],
            disabled && redoRadioCss.disabled,
          )}
          justify="center"
          style={
            dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
              ? {
                  borderColor: selected
                    ? dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.borderColor
                    : dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.borderColor,
                }
              : undefined
          }
        >
          <span
            className={redoRadioCss.circleInner}
            style={
              dangerousStyleThatShouldOnlyBeUsedForMerchantBranding && selected
                ? {
                    backgroundColor:
                      dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.color ||
                      dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.borderColor,
                  }
                : undefined
            }
          />
        </Flex>
      </Flex>
      {showDescriptors && (
        <Flex
          dir="column"
          flex={fullWidth ? 1 : undefined}
          gap="none"
          w={fullWidth ? "full" : undefined}
        >
          {labelBadge ? (
            <Flex align="center" gap="xs">
              {labelNode}
              {labelBadge}
            </Flex>
          ) : (
            labelNode
          )}
          {description && (
            <Text
              className={redoRadioCss.text}
              fontSize={sizeToDescriptionTextSize[size]}
              style={textStyle}
              textColor={
                !dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
                  ? descriptionTextColor
                  : undefined
              }
            >
              {description}
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  );

  if (disabled && disabledTooltip) {
    return (
      <DisabledTooltip disabledMessage={disabledTooltip}>
        {radio}
      </DisabledTooltip>
    );
  }

  return radio;
});

const sizeToClass: Record<RedoRadioButtonSize, string | undefined> = {
  xs: redoRadioCss.xs,
  sm: redoRadioCss.sm,
  md: redoRadioCss.md,
};

export const sizeToLabelTextSize: Record<RedoRadioButtonSize, TextSizeValue> = {
  ["xs"]: "xs",
  ["sm"]: "sm",
  ["md"]: "md",
};

export const sizeToDescriptionTextSize: Record<
  RedoRadioButtonSize,
  TextSizeValue
> = { ["xs"]: "xxs", ["sm"]: "xs", ["md"]: "sm" };
